/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ReactNode } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { cssMap, cx, jsx } from '@atlaskit/css';
import Lozenge from '@atlaskit/lozenge';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import type { AgentScenarioAction } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { getAgentActionIcon } from '../../../utils/agent-actions/index.tsx';

import messages from './messages.ts';

type AgentActionPillAction = AgentScenarioAction & {
	icon?: ReactNode;
};

const styles = cssMap({
	wrapper: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small', '0.25rem'),
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
		width: 'fit-content',
	},
	wrapperInactive: {
		opacity: token('opacity.disabled'),
	},

	pillIcon: {
		borderRadius: token('radius.small', '0.25rem'),
		width: '20px',
		height: '20px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},

	imageIcon: {
		objectFit: 'contain',
		width: '20px',
		height: '20px',
	},
});

export const AgentActionPill = ({
	action: { displayName, id, tags, icon, iconUrl },
	isActive,
}: {
	action: AgentActionPillAction;
	isActive: boolean;
}): JSX.Element => {
	di(getAgentActionIcon);

	const { formatMessage } = useIntl();

	return (
		<Tooltip
			content={formatMessage(messages.inactiveActionTooltip)}
			position="right"
			canAppear={() => !isActive}
		>
			{(tooltipProps) => (
				<Box {...tooltipProps} xcss={cx(styles.wrapper, !isActive && styles.wrapperInactive)}>
					<Inline space="space.050" alignBlock="center">
						<Box xcss={styles.pillIcon} backgroundColor="color.background.brand.subtlest">
							{icon ? (
								icon
							) : iconUrl ? (
								<Box as="img" src={iconUrl} alt="" xcss={styles.imageIcon} />
							) : (
								getAgentActionIcon(id)
							)}
						</Box>
						<Text>
							{displayName}{' '}
							{tags?.includes('beta') && (
								<Lozenge appearance="discovery">{formatMessage(messages.betaTag)}</Lozenge>
							)}
						</Text>
					</Inline>
				</Box>
			)}
		</Tooltip>
	);
};

export function ToolPill({
	displayName,
	iconUrl,
}: {
	displayName: string;
	iconUrl: string;
}): JSX.Element {
	return (
		<Inline xcss={styles.wrapper} space="space.050" alignBlock="center">
			<Box xcss={styles.pillIcon} backgroundColor="color.background.brand.subtlest">
				<Box as="img" src={iconUrl} alt={displayName} xcss={styles.imageIcon} />
			</Box>
			<Text>{displayName}</Text>
		</Inline>
	);
}
